import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import InternalLinks from '@/components/seo/InternalLinks';
import {
  Building2, BarChart3, Globe, TrendingUp, Target,
  Star, CheckCircle2, XCircle, Gift, CreditCard,
  Smartphone, Headphones, Shield, Trophy, ChevronDown, ChevronUp,
  Zap, Monitor, Banknote, Languages, Coins, Check, X,
  Laptop, TabletSmartphone, Apple, Chrome
} from 'lucide-react';

const API_BASE = '';

/* ── Star Rating Visual ─────────────────────────────── */
function StarRating({ rating, max = 5, size = "sm" }) {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.3;
  const sz = size === "lg" ? "w-5 h-5" : "w-3.5 h-3.5";
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <Star key={i} className={`${sz} ${
          i < full ? 'text-amber-400 fill-amber-400'
            : i === full && hasHalf ? 'text-amber-400 fill-amber-400/50'
            : 'text-slate-700'
        }`} />
      ))}
    </div>
  );
}

/* ── Rating Distribution Bar ─────────────────────────── */
function RatingDistBar({ label, percent, color = "emerald" }) {
  const barColors = {
    emerald: "bg-emerald-500", amber: "bg-amber-500", blue: "bg-blue-500",
    cyan: "bg-cyan-500", rose: "bg-rose-500",
  };
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-3 text-slate-500 tabular-nums text-right">{label}</span>
      <Star className="w-2.5 h-2.5 text-amber-500 fill-amber-500" />
      <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${barColors[color] || barColors.emerald} transition-all duration-700`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="w-8 text-right text-slate-400 tabular-nums">{percent}%</span>
    </div>
  );
}

/* ── Detailed Rating Card ────────────────────────────── */
function DetailedRatingCard({ name, data, color = "emerald" }) {
  const [expanded, setExpanded] = useState(false);
  if (!data) return null;

  const { score, above_avg, count, dist } = data;
  const badgeColor = above_avg >= 20 ? 'text-emerald-400 bg-emerald-400/10 border-emerald-500/20'
    : above_avg >= 10 ? 'text-cyan-400 bg-cyan-400/10 border-cyan-500/20'
    : 'text-slate-400 bg-slate-400/10 border-slate-500/20';

  return (
    <div className="bg-slate-900/40 border border-slate-700/40 rounded-xl overflow-hidden transition-all duration-300">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-800/30 transition-colors"
        data-testid={`rating-card-${name}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <span className="text-lg font-bold text-slate-200 tabular-nums">{score}</span>
            <StarRating rating={score} />
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-slate-300">{name}</p>
            <p className="text-[10px] text-slate-500">{count} ratings</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${badgeColor}`}>
            {above_avg > 0 ? '+' : ''}{above_avg}%
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </button>

      {expanded && dist && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-800/50 space-y-1.5">
          <RatingDistBar label="5" percent={dist[0]} color="emerald" />
          <RatingDistBar label="4" percent={dist[1]} color="cyan" />
          <RatingDistBar label="3" percent={dist[2]} color="amber" />
          <RatingDistBar label="2" percent={dist[3]} color="rose" />
          <RatingDistBar label="1" percent={dist[4]} color="rose" />
        </div>
      )}
    </div>
  );
}

/* ── Products & Platforms Table ───────────────────────── */
function ProductsPlatforms({ data, t }) {
  if (!data) return null;
  const { platforms, categories } = data;
  if (!platforms || !categories) return null;

  const platformIcons = {
    'Web': <Globe className="w-5 h-5" />,
    'Android': <TabletSmartphone className="w-5 h-5" />,
    'iOS': <Apple className="w-5 h-5" />,
    'Windows': <Laptop className="w-5 h-5" />,
    'Mac': <Monitor className="w-5 h-5" />,
  };

  return (
    <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl overflow-hidden" data-testid="products-platforms">
      <div className="p-5 pb-3">
        <div className="flex items-center gap-2 mb-4">
          <Smartphone className="w-4 h-4 text-violet-400" />
          <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider"
            style={{ fontFamily: 'var(--font-heading)' }}>{t('seo.productsApps', 'Products & Platforms')}</h3>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800/50">
              <th className="text-left px-5 py-3 text-xs text-slate-500 uppercase tracking-wider font-medium w-40">
                {t('seo.feature', 'Feature')}
              </th>
              {platforms.map(p => (
                <th key={p} className="px-3 py-3 text-center">
                  <div className="flex flex-col items-center gap-1.5">
                    <span className="text-slate-400">{platformIcons[p] || <Globe className="w-5 h-5" />}</span>
                    <span className="text-[10px] text-slate-500 font-medium">{p}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat, ci) => (
              <React.Fragment key={`cat-group-${ci}`}>
                <tr className="bg-slate-800/20">
                  <td colSpan={platforms.length + 1} className="px-5 py-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{cat.name}</span>
                  </td>
                </tr>
                {cat.features.map((feat, fi) => (
                  <tr key={`feat-${ci}-${fi}`} className="border-b border-slate-800/30 hover:bg-slate-800/20 transition-colors">
                    <td className="px-5 py-2.5 text-xs text-slate-400">{feat.name}</td>
                    {feat.availability.map((avail, ai) => (
                      <td key={ai} className="px-3 py-2.5 text-center">
                        {avail ? (
                          <Check className="w-4 h-4 text-emerald-400 mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-rose-500/50 mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Ratings Grid (Compact Characteristics) ──────────── */
function RatingsGrid({ ratings, t }) {
  if (!ratings) return null;
  const ITEMS = [
    { key: 'odds_quality', label: t('seo.rOdds', 'Odds'), icon: Target },
    { key: 'markets_coverage', label: t('seo.rMarkets', 'Markets'), icon: BarChart3 },
    { key: 'sports_coverage', label: t('seo.rSports', 'Sports'), icon: Trophy },
    { key: 'bonuses_promotions', label: t('seo.rBonuses', 'Bonuses'), icon: Gift },
    { key: 'customer_service', label: t('seo.rSupport', 'Support'), icon: Headphones },
    { key: 'safety_fairness', label: t('seo.rSafety', 'Safety'), icon: Shield },
    { key: 'betting_limits', label: t('seo.rLimits', 'Limits'), icon: Banknote },
    { key: 'website_usability', label: t('seo.rUsability', 'Usability'), icon: Monitor },
  ];
  const valid = ITEMS.filter(r => ratings[r.key]);
  if (!valid.length) return null;

  return (
    <div className="bg-gradient-to-br from-blue-950/30 to-slate-900/40 border border-blue-800/20 rounded-xl p-5" data-testid="ratings-grid">
      <div className="flex items-center gap-2 mb-4">
        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider"
          style={{ fontFamily: 'var(--font-heading)' }}>{t('seo.characteristics', 'Characteristics')}</h3>
        {ratings.overall && (
          <span className="ml-auto text-xs font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">
            {ratings.overall}/5
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
        {valid.map(item => {
          const Icon = item.icon;
          return (
            <div key={item.key} className="flex items-center gap-2">
              <Icon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-slate-500 truncate">{item.label}</p>
                <div className="flex items-center gap-1">
                  <StarRating rating={ratings[item.key]} />
                  <span className="text-xs font-bold text-slate-300 tabular-nums ml-1">{ratings[item.key]}/5</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Quick Facts Grid ────────────────────────────────── */
function QuickFacts({ facts, t }) {
  if (!facts) return null;
  const ITEMS = [
    { key: 'company_name', label: t('seo.fCompany', 'Company'), icon: Building2 },
    { key: 'founded', label: t('seo.fFounded', 'Founded'), icon: Zap },
    { key: 'license', label: t('seo.fLicense', 'License'), icon: Shield },
    { key: 'website', label: t('seo.fWebsite', 'Website'), icon: Globe },
    { key: 'languages_count', label: t('seo.fLanguages', 'Languages'), icon: Languages },
    { key: 'currencies_count', label: t('seo.fCurrencies', 'Currencies'), icon: Coins },
    { key: 'sports_count', label: t('seo.fSports', 'Sports'), icon: Trophy },
    { key: 'min_deposit', label: t('seo.fMinDeposit', 'Min Deposit'), icon: Banknote },
  ];
  const FLAGS = [
    { key: 'live_betting', label: t('seo.fLive', 'Live Betting') },
    { key: 'live_streaming', label: t('seo.fStreaming', 'Streaming') },
    { key: 'cash_out', label: t('seo.fCashOut', 'Cash Out') },
    { key: 'mobile_app', label: t('seo.fMobile', 'Mobile App') },
    { key: 'crypto_supported', label: t('seo.fCrypto', 'Crypto') },
  ];

  return (
    <div className="bg-gradient-to-br from-blue-950/30 to-slate-900/40 border border-blue-800/20 rounded-xl p-5" data-testid="quick-facts">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-4 h-4 text-cyan-400" />
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider"
          style={{ fontFamily: 'var(--font-heading)' }}>{t('seo.quickFacts', 'Quick Facts')}</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {ITEMS.map(item => {
          const val = facts[item.key];
          if (!val) return null;
          const Icon = item.icon;
          return (
            <div key={item.key} className="flex items-start gap-2">
              <div className="w-7 h-7 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0 mt-0.5">
                <Icon className="w-3.5 h-3.5 text-cyan-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{item.label}</p>
                <p className="text-xs font-medium text-slate-300 truncate">{String(val)}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-800/50">
        {FLAGS.map(f => {
          const active = facts[f.key];
          return (
            <span key={f.key} className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full ${
              active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                : 'bg-slate-800/50 text-slate-500 border border-slate-700/30'
            }`}>
              {active ? <CheckCircle2 className="w-2.5 h-2.5" /> : <XCircle className="w-2.5 h-2.5" />}
              {f.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* ── Pros & Cons ─────────────────────────────────────── */
function ProsCons({ pros, cons, t }) {
  if ((!pros || !pros.length) && (!cons || !cons.length)) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pros-cons-section">
      {pros && pros.length > 0 && (
        <div className="bg-slate-900/40 border border-emerald-500/20 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-semibold text-emerald-300 uppercase tracking-wider"
              style={{ fontFamily: 'var(--font-heading)' }}>{t('seo.pros', 'Pros')}</h3>
          </div>
          <ul className="space-y-2">
            {pros.map((p, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-400">
                <CheckCircle2 className="w-4 h-4 text-emerald-500/70 shrink-0 mt-0.5" />
                <span className="leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {cons && cons.length > 0 && (
        <div className="bg-slate-900/40 border border-rose-500/20 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <XCircle className="w-4 h-4 text-rose-400" />
            <h3 className="text-sm font-semibold text-rose-300 uppercase tracking-wider"
              style={{ fontFamily: 'var(--font-heading)' }}>{t('seo.cons', 'Cons')}</h3>
          </div>
          <ul className="space-y-2">
            {cons.map((c, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-400">
                <XCircle className="w-4 h-4 text-rose-500/70 shrink-0 mt-0.5" />
                <span className="leading-relaxed">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ── Content Block ───────────────────────────────────── */
function ContentBlock({ icon: Icon, title, content, color = "emerald" }) {
  if (!content || content.trim().length < 20) return null;
  const styles = {
    emerald: "border-emerald-500/15 bg-emerald-500/5", amber: "border-amber-500/15 bg-amber-500/5",
    blue: "border-blue-500/15 bg-blue-500/5", cyan: "border-cyan-500/15 bg-cyan-500/5",
    purple: "border-violet-500/15 bg-violet-500/5", rose: "border-rose-500/15 bg-rose-500/5",
    teal: "border-teal-500/15 bg-teal-500/5",
  };
  const iconStyles = {
    emerald: "text-emerald-400", amber: "text-amber-400", blue: "text-blue-400",
    cyan: "text-cyan-400", purple: "text-violet-400", rose: "text-rose-400", teal: "text-teal-400",
  };
  return (
    <div className={`border rounded-xl p-4 ${styles[color] || styles.emerald}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${iconStyles[color] || iconStyles.emerald}`} />
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider"
          style={{ fontFamily: 'var(--font-heading)' }}>{title}</h3>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed">{content}</p>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────── */
export default function BookmakerSEOContent({ slug }) {
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_BASE}/api/seo/bookmaker/${slug}?lang=${currentLang}`)
      .then(r => r.json())
      .then(data => { setSeoData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, currentLang]);

  if (loading || !seoData) return null;
  if (seoData.fallback) return null;
  const blocks = seoData.content_blocks || {};
  const hasContent = Object.values(blocks).some(v =>
    (typeof v === 'string' && v.trim().length >= 50 && !v.includes('Compare')) ||
    (Array.isArray(v) && v.length > 0)
  );
  if (!hasContent) return null;

  const ratings = seoData.structured_ratings || null;
  const facts = seoData.quick_facts || null;
  const ratingDists = seoData.rating_distributions || null;
  const products = seoData.products_platforms || null;

  const RATING_LABELS = {
    odds_quality: t('seo.rOdds', 'Odds Quality'),
    markets_coverage: t('seo.rMarkets', 'Markets Coverage'),
    sports_coverage: t('seo.rSports', 'Sports Coverage'),
    bonuses_promotions: t('seo.rBonuses', 'Bonuses & Promotions'),
    customer_service: t('seo.rSupport', 'Customer Service'),
    safety_fairness: t('seo.rSafety', 'Safety & Fairness'),
    betting_limits: t('seo.rLimits', 'Betting Limits'),
    website_usability: t('seo.rUsability', 'Website Usability'),
    website_look: t('seo.rLook', 'Website Design'),
    website_functionality: t('seo.rFunc', 'Functionality'),
  };

  const DETAIL_BLOCKS = [
    { key: 'coverage_summary', icon: Globe, title: t('seo.coverage', 'Sports Coverage'), color: 'blue' },
    { key: 'market_presence', icon: TrendingUp, title: t('seo.marketPresence', 'Market Presence'), color: 'cyan' },
    { key: 'odds_comparison_context', icon: Target, title: t('seo.oddsComparison', 'Odds & Margins'), color: 'purple' },
    { key: 'popular_matches', icon: Trophy, title: t('seo.popularMatches', 'Popular Events'), color: 'emerald' },
  ];

  const EXTRA_BLOCKS = [
    { key: 'bonus_info', icon: Gift, title: t('seo.bonusInfo', 'Bonuses & Promotions'), color: 'amber' },
    { key: 'payment_methods', icon: CreditCard, title: t('seo.paymentMethods', 'Payment Methods'), color: 'teal' },
    { key: 'mobile_experience', icon: Smartphone, title: t('seo.mobileExperience', 'Mobile'), color: 'blue' },
    { key: 'customer_support', icon: Headphones, title: t('seo.customerSupport', 'Support'), color: 'cyan' },
    { key: 'licensing_safety', icon: Shield, title: t('seo.licensingSafety', 'License & Safety'), color: 'rose' },
  ];

  const visibleDetail = DETAIL_BLOCKS.filter(b => blocks[b.key] && blocks[b.key].trim().length >= 20);
  const visibleExtra = EXTRA_BLOCKS.filter(b => blocks[b.key] && blocks[b.key].trim().length >= 20);

  return (
    <div className="space-y-4 mb-6 relative" data-testid="bookmaker-seo-content">
      {/* Subtle 1xBet-themed blue glow background */}
      <div className="absolute -inset-4 rounded-3xl bg-gradient-to-b from-blue-950/30 via-blue-950/10 to-transparent pointer-events-none -z-10" />

      {/* 1. Overview */}
      {blocks.bookmaker_overview && blocks.bookmaker_overview.trim().length >= 20 && (
        <div className="bg-gradient-to-br from-blue-950/40 to-slate-900/40 border border-blue-800/20 rounded-xl p-5" data-testid="bookmaker-overview-block">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider"
              style={{ fontFamily: 'var(--font-heading)' }}>{t('seo.bookmakerOverview', 'Overview')}</h3>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">{blocks.bookmaker_overview}</p>
        </div>
      )}

      {/* 2. Ratings Grid */}
      <RatingsGrid ratings={ratings} t={t} />

      {/* 3. Quick Facts */}
      <QuickFacts facts={facts} t={t} />

      {/* 4. Pros & Cons */}
      <ProsCons pros={blocks.pros_list} cons={blocks.cons_list} t={t} />

      {/* 5. Rating Analysis Text */}
      {blocks.rating_summary && blocks.rating_summary.trim().length >= 20 && (
        <div className="bg-slate-900/40 border border-amber-500/15 rounded-xl p-5" data-testid="rating-summary-block">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider"
              style={{ fontFamily: 'var(--font-heading)' }}>{t('seo.ratingSummary', 'Rating Analysis')}</h3>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">{blocks.rating_summary}</p>
        </div>
      )}

      {/* 6. Detail Blocks */}
      {visibleDetail.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleDetail.map(b => (
            <ContentBlock key={b.key} icon={b.icon} title={b.title} content={blocks[b.key]} color={b.color} />
          ))}
        </div>
      )}

      {/* 7. Extra Blocks */}
      {visibleExtra.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleExtra.map(b => (
            <ContentBlock key={b.key} icon={b.icon} title={b.title} content={blocks[b.key]} color={b.color} />
          ))}
        </div>
      )}

      {/* 8. Detailed Rating Distributions (expandable cards) */}
      {ratingDists && Object.keys(ratingDists).length > 0 && (
        <div data-testid="detailed-ratings-section">
          <div className="flex items-center gap-2 mb-3 px-1">
            <BarChart3 className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider"
              style={{ fontFamily: 'var(--font-heading)' }}>{t('seo.detailedRatings', 'Detailed Ratings')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(ratingDists).map(([key, data]) => (
              <DetailedRatingCard key={key} name={RATING_LABELS[key] || key} data={data} />
            ))}
          </div>
        </div>
      )}

      {/* 9. Products & Platforms Table */}
      <ProductsPlatforms data={products} t={t} />

      <InternalLinks links={seoData.internal_links} />
    </div>
  );
}
